import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { updateUserRole } from "../../actions/user.actions";
import { roleOptions } from "../../store/localItems";

const EditRoleUserBlock = ({
    userDetails = {},
    setTypeModal,
    isSuperAdmin,
    registerGuard,
    onNavigate,
}) => {
    const dispatch = useDispatch();
    const options = useMemo(
        () => roleOptions.filter((option) => option.value !== "superAdmin"),
        []
    );
    const [initialRole, setInitialRole] = useState(userDetails.role || "user");
    const [role, setRole] = useState(userDetails.role || "user");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    useEffect(() => {
        const next = userDetails.role || "user";
        setInitialRole(next);
        setRole(next);
        setError("");
        setMessage("");
    }, [userDetails?._id, userDetails?.role]);

    const isDirty = role !== initialRole;
    const roleName = (value) => options.find((option) => option.value === value)?.name || value || "—";

    const save = useCallback(async () => {
        if (!isSuperAdmin || userDetails.role === "superAdmin" || busy) return false;

        setBusy(true);
        setError("");
        setMessage("");

        const result = await dispatch(updateUserRole({ id: userDetails._id, role }));

        setBusy(false);

        if (!result?.ok) {
            setError(result?.message || "Не удалось изменить роль");
            return false;
        }

        setInitialRole(role);
        setMessage(result?.message || "Роль пользователя изменена");
        return true;
    }, [busy, dispatch, isSuperAdmin, role, userDetails._id, userDetails.role]);

    const discard = useCallback(() => {
        setRole(initialRole);
        setError("");
        setMessage("");
        return true;
    }, [initialRole]);

    useEffect(() => {
        registerGuard?.({
            isDirty,
            canSave: isDirty && !busy,
            title: "Закрыть без сохранения роли?",
            text: "Вы выбрали другую роль, но ещё не сохранили изменение.",
            details: isDirty ? [{
                label: "Роль пользователя",
                before: roleName(initialRole),
                after: roleName(role),
            }] : [],
            onSave: save,
            onDiscard: discard,
        });

        return () => registerGuard?.(null);
    }, [busy, discard, initialRole, isDirty, registerGuard, role, save]);

    const saveAndReturn = async () => {
        const saved = await save();
        if (saved) setTypeModal("");
    };

    return (
        <div className="userSection">
            <div className="userSection__head">
                <div>
                    <span>Доступ</span>
                    <h3>Роль и права</h3>
                    <p>Системная роль определяет доступ пользователя к административным возможностям.</p>
                </div>
                {isDirty ? <b className="userSection__dirty">Есть изменения</b> : null}
            </div>

            <div className="userManageForm">
                <div className="userManageForm__notice">
                    Роль <b>superAdmin</b> нельзя назначить через управление пользователями.
                </div>

                <label className="userManageForm__field">
                    <span>Роль</span>
                    <select
                        value={role}
                        onChange={(event) => {
                            setRole(event.target.value);
                            setError("");
                            setMessage("");
                        }}
                        disabled={busy}
                    >
                        {options.map((option) => (
                            <option key={option.value} value={option.value}>{option.name}</option>
                        ))}
                    </select>
                </label>

                {message ? <div className="userManageForm__message">{message}</div> : null}
                {error ? <div className="userManageForm__error">{error}</div> : null}

                <div className="userManageForm__footer">
                    <button type="button" className="is-secondary" onClick={() => onNavigate?.("")} disabled={busy}>
                        Отмена
                    </button>
                    <button type="button" onClick={saveAndReturn} disabled={busy || !isDirty}>
                        {busy ? "Сохранение..." : "Сохранить роль"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditRoleUserBlock;
